/** @module Start screen: what the collection is worth, what it cost and what still needs a decision. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ScanLine } from "lucide-react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { PageHeader } from "@/components/page-header";
import {
  DuplicatesPanel,
  MoversPanel,
  RecentPanel,
  ReviewPanel,
} from "@/features/dashboard/panels";
import { ProfitPanel } from "@/features/dashboard/profit-panel";
import { ValueCard } from "@/features/dashboard/value-card";

async function fetchSummary() {
  const { data, response } = await api.GET("/api/v1/dashboard/summary", {});
  if (!data) throw problemFrom(response);
  return data;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const summary = useQuery({ queryKey: ["dashboard", "summary"], queryFn: fetchSummary });

  let body;
  if (summary.isPending) {
    body = <p className="t-muted">{t("common.loading")}</p>;
  } else if (summary.isError) {
    body = (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );
  } else {
    const data = summary.data;
    body = (
      <>
        {/* Phone: one column, in the order of the design's start screen. */}
        <div className="flex flex-col gap-[14px] md:hidden">
          <ValueCard summary={data} compact />
          <Link to="/scan" className="scan-hero">
            <span className="flex flex-col gap-[2px]">
              <b className="text-[17px]">{t("dashboard.scanTitle")}</b>
              <span className="t-sm opacity-75">{t("dashboard.scanHint")}</span>
            </span>
            <ScanLine />
          </Link>
          <ReviewPanel open={data.open_reviews} compact />
          <ProfitPanel profit={data.profit} compact />
          <RecentPanel strip />
          <MoversPanel />
        </div>
        {/* Desktop: value beside reviews and duplicates, movers beside the latest cards. */}
        <div className="hidden flex-col gap-5 md:flex">
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-5">
            <div className="panel panel-lg">
              <ValueCard summary={data} />
            </div>
            <div className="flex flex-col gap-5">
              <ReviewPanel open={data.open_reviews} />
              <ProfitPanel profit={data.profit} />
              <DuplicatesPanel />
            </div>
          </div>
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-5">
            <MoversPanel />
            <RecentPanel />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      <PageHeader title={t("dashboard.title")} />
      {body}
    </div>
  );
}
